import { useMelwaysInfo } from '@seek/melways-react';
import { useTranslations } from '@vocab/react';
import {
  Box,
  ButtonLink,
  Divider,
  Heading,
  Text,
  TextLink,
} from 'braid-design-system';
import { useCallback, type ReactNode } from 'react';

import { useBrandName } from 'src/hooks/useBrandName';
import { useReturnUrl } from 'src/hooks/useReturnUrl';
import { useAnalyticsFacade } from 'src/modules/AnalyticsFacade';

import translations from './.vocab';
import { GoogleIcon } from './images/GoogleIcon';
import promoteSignInImageAU from './images/au.jpg';
import promoteSignInImageHK from './images/hk.jpg';
import promoteSignInImageMYID from './images/my_id.jpg';
import promoteSignInImageNZ from './images/nz.jpg';
import promoteSignInImagePHTH from './images/ph_th.jpg';
import promoteSignInImageSG from './images/sg.jpg';

import * as styleRefs from './SignedOutDashboard.css';

const actionOrigin = 'homepage hero banner';
const usePromoteSignInImage = () => {
  const { country } = useMelwaysInfo();
  switch (country) {
    case 'au':
      return promoteSignInImageAU;
    case 'nz':
      return promoteSignInImageNZ;
    case 'sg':
      return promoteSignInImageSG;
    case 'my':
    case 'id':
      return promoteSignInImageMYID;
    case 'ph':
    case 'th':
      return promoteSignInImagePHTH;
    case 'hk':
      return promoteSignInImageHK;
    default:
      return promoteSignInImageAU;
  }
};

const PromoteSignInSectionView = ({
  header,
  content,
  imageSrc,
  widerWidget,
}: {
  header: string | ReactNode;
  content: ReactNode;
  imageSrc: string;
  widerWidget?: boolean;
}) => (
  <Box
    display="flex"
    alignItems="center"
    flexDirection={{ mobile: 'column', tablet: 'row' }}
    position="relative"
    gap="large"
    className={styleRefs.rootContainer}
  >
    <Box
      background="surface"
      borderRadius="large"
      paddingY="large"
      paddingX={{ tablet: 'large' }}
      display="flex"
      flexDirection="column"
      gap="large"
      zIndex={1}
      className={[
        styleRefs.widgetContainer,
        {
          [styleRefs.widgetContainerWider]: widerWidget,
        },
      ]}
      position="relative"
    >
      <Heading level="2">{header}</Heading>
      {content}
    </Box>
    <Box
      overflow="hidden"
      position={{ mobile: 'relative', tablet: 'absolute' }}
      right={0}
      height="full"
      borderRadius="large"
      className={styleRefs.imageContainer}
    >
      <Box component="img" src={imageSrc} className={styleRefs.image} alt="" />
    </Box>
  </Box>
);

const SignedOutDashboard = () => {
  const { t } = useTranslations(translations);
  const registerLink = useReturnUrl({ mode: 'register' });
  const signInLink = useReturnUrl({ mode: 'signin' });
  const promoteSignInImage = usePromoteSignInImage();
  const brandName = useBrandName();
  const analyticsFacade = useAnalyticsFacade();
  const SIGN_IN = t('Sign in');
  const GOOGLE_SIGN_IN = t('Continue with Google');
  const REGISTER = t('Register');

  const onClickSignIn = useCallback(
    (linkText: string) => {
      analyticsFacade.signInPressed({
        linkContext: {
          linkPosition: 'body',
          linkText,
        },
        actionOrigin,
      });
    },
    [analyticsFacade],
  );

  const onClickRegister = useCallback(() => {
    analyticsFacade.registerPressed({
      linkContext: {
        linkPosition: 'body',
        linkText: REGISTER,
      },
      actionOrigin,
    });
  }, [analyticsFacade, REGISTER]);

  return (
    <PromoteSignInSectionView
      header={t('Find the right job for you on {brand}', { brand: brandName })}
      content={
        <>
          <Text>{t('Sign in to see the right jobs for you')}</Text>
          <Box position="relative">
            <ButtonLink
              href={`${signInLink}&strategy=google`}
              onClick={() => onClickSignIn(GOOGLE_SIGN_IN)}
              rel="nofollow"
            >
              <Box
                position="absolute"
                left={0}
                marginLeft={{ mobile: 'small', tablet: 'gutter' }}
              >
                <GoogleIcon />
              </Box>
              <Box paddingLeft={{ mobile: 'small', tablet: 'none' }}>
                {GOOGLE_SIGN_IN}
              </Box>
            </ButtonLink>
          </Box>
          <Box display="flex" gap="small" alignItems="center">
            <Divider />
            <Text tone="secondary">{t('or')}</Text>
            <Divider />
          </Box>
          <ButtonLink
            tone="formAccent"
            href={signInLink}
            onClick={() => onClickSignIn(SIGN_IN)}
            rel="nofollow"
            data={{ automation: 'promoteSignIn' }}
          >
            {SIGN_IN}
          </ButtonLink>
          <Text>
            {t('Don’t have an account?')}{' '}
            <TextLink
              href={registerLink}
              onClick={onClickRegister}
              rel="nofollow"
            >
              {REGISTER}
            </TextLink>
          </Text>
        </>
      }
      imageSrc={promoteSignInImage}
    />
  );
};

export default SignedOutDashboard;
